import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AuthImage } from "@/components/AuthMedia";
import { useLang } from "@/context/LanguageContext";
import { GradeBadge, PaceFlag, StatusChip } from "@/components/Badges";
import { whatsappShare, inspectionShareText } from "@/lib/whatsapp";
import { ArrowLeft, Camera, User, Share2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function LoomDetail() {
  const { loomId } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [loom, setLoom] = useState(null);
  const [po, setPo] = useState(null);
  const [inspections, setInspections] = useState([]);

  useEffect(() => {
    (async () => {
      const looms = await api.get("/looms");
      const l = looms.data.find((x) => x.id === loomId);
      setLoom(l);
      if (l?.current_po_id) {
        const pr = await api.get(`/pos/${l.current_po_id}`);
        setPo(pr.data);
      }
      const ir = await api.get("/inspections", { params: { loom_id: loomId } });
      setInspections(ir.data);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loomId]);

  if (!loom) return <div className="text-sm text-[#7C8E9E]">{t("loading")}</div>;

  const shareInspection = (ins) => {
    const text = inspectionShareText({
      lang,
      loomNumber: loom.loom_number,
      weaverName: loom.weaver_name,
      poNumber: po?.po_number,
      milestone: ins.milestone,
      percent: ins.percent_complete,
      grade: ins.grade,
      defects: (ins.defects || []).length,
    });
    whatsappShare(text);
  };

  return (
    <div>
      <button data-testid="loom-back-btn" onClick={() => nav(-1)} className="text-sm flex items-center gap-1 mb-3 text-[#4A627A]">
        <ArrowLeft size={16} /> Back
      </button>
      <div className="hag-surface p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="eyebrow">{t("loom_no")}</div>
            <div className="text-3xl font-bold" style={{ color: "var(--brand-navy)" }}>{loom.loom_number}</div>
            <div className="text-sm text-[#4A627A]">{loom.weaver_name} · {loom.village} · {t("zone")} {loom.zone || "—"}</div>
            {user?.role !== "inspector" && loom.weaver_id && (
              <Link data-testid="view-weaver-profile" to={`/weaver/${loom.weaver_id}`} className="text-xs text-[#1B3A5C] underline mt-1 inline-flex items-center gap-1">
                <User size={12} /> View weaver profile
              </Link>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusChip status={loom.status} />
            <GradeBadge grade={loom.last_grade} />
          </div>
        </div>

        {po && (
          <div className="mt-5 border-t border-[#1B3A5C]/10 pt-4">
            <div className="eyebrow">{t("po_number")}</div>
            <Link to={`/po/${po.id}`} className="text-lg font-bold" style={{ color: "var(--brand-navy)" }}>{po.po_number}</Link>
            <div className="flex flex-wrap gap-3 text-sm mt-2">
              <div><span className="text-[#7C8E9E]">{t("eta")}: </span>{po.eta ? new Date(po.eta).toLocaleDateString() : "—"}</div>
              <div><span className="text-[#7C8E9E]">%: </span>{po.percent_complete}%</div>
              <PaceFlag flag={po.pace_flag} />
            </div>
          </div>
        )}

        <button
          data-testid="start-inspection-btn"
          onClick={() => nav(`/inspector/loom/${loom.id}/inspect`)}
          className="mt-6 w-full h-14 rounded-md font-semibold text-white text-lg flex items-center justify-center gap-2"
          style={{ background: "var(--brand-navy)" }}
          disabled={loom.status === "Stopped"}
        >
          <Camera size={20} /> {t("start_inspection")}
        </button>
        {loom.status === "Stopped" && (
          <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
            {lang === "hi" ? "लूम रुका हुआ है — सुपरवाइज़र से शिकायत हल होने का इंतज़ार।" : "Loom is Stopped — awaiting supervisor resolution of critical escalation."}
          </div>
        )}
      </div>

      <h2 className="mt-6 font-bold text-lg" style={{ color: "var(--brand-navy)" }}>{lang === "hi" ? "निरीक्षण इतिहास" : "Inspection history"}</h2>
      <div className="mt-2 space-y-2">
        {inspections.length === 0 && <div className="text-sm text-[#7C8E9E]">{lang === "hi" ? "अभी तक कोई निरीक्षण नहीं।" : "No inspections yet."}</div>}
        {inspections.map((ins) => (
          <div key={ins.id} data-testid={`inspection-row-${ins.id}`} className="hag-surface p-3 flex items-center gap-3 flex-wrap">
            <div className="text-sm font-semibold min-w-[120px]">{ins.milestone}</div>
            <div className="text-xs text-[#7C8E9E]">{new Date(ins.date).toLocaleString()}</div>
            <GradeBadge grade={ins.grade} />
            <div className="flex gap-1">
              {(ins.photos || []).slice(0, 3).map((p, i) => (
                p.file_id ? (
                  <AuthImage key={i} fileId={p.file_id} alt="inspection" className="w-10 h-10 object-cover rounded border border-[#1B3A5C]/10" />
                ) : null
              ))}
            </div>
            <button
              data-testid={`share-ins-${ins.id}`}
              onClick={() => shareInspection(ins)}
              className="ml-auto tap-target px-2 rounded-md text-xs border border-[#1B3A5C]/15 flex items-center gap-1"
              title="Share to WhatsApp"
            >
              <Share2 size={12} /> WhatsApp
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
